import React, { useEffect, useMemo, useState } from 'react'
import { useWallet } from '../../contexts/WalletContext'
import { getAffiliateMembers } from '../../services/affiliateService'
import type { AffiliateMemberItem } from '../../services/affiliateService'

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateMemberPage: React.FC = () => {
  const { formatCurrency } = useWallet()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])
  const [searchTerm, setSearchTerm] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const list = await getAffiliateMembers()
        if (!cancelled) setMembers(list)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [])

  const memberStats = {
    totalMembers: members.length,
    totalBetAmount: 0 // Backend does not provide per-referral bet sum yet
  }

  const filteredMembers = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase()
    if (!keyword) return members
    return members.filter((member) => {
      const username = String(member.username || '').toLowerCase()
      const displayName = String(member.displayName || '').toLowerCase()
      const maskedUsername = maskMember(member.username || '').toLowerCase()
      return username.includes(keyword) || displayName.includes(keyword) || maskedUsername.includes(keyword)
    })
  }, [members, searchTerm])

  if (loading) {
    return (
      <div className="text-center py-3">
        <div className="spinner-border text-primary" role="status" />
        <p className="mt-2">กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="text-center py-3">
        <p className="c-red">{error}</p>
      </div>
    )
  }

  return (
    <div className="mb-2 text-center">
      <div className="row mx--1">
        <div className="col-6 px-1">
          <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
            <div className="bgc-4 px-1 py-1 t-regular bdr-5">
              <small>จำนวนสมาชิก (คน)</small>
            </div>
            <div className="px-1 t-regular c-white">
              <h4>{memberStats.totalMembers}</h4>
            </div>
          </div>
        </div>

        <div className="col-6 px-1">
          <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
            <div className="bgc-4 px-1 py-1 t-regular bdr-5">
              <small>ยอดแทงรวม (บาท)</small>
            </div>
            <div className="px-1 t-regular c-white">
              <h4>{formatCurrency(memberStats.totalBetAmount)}</h4>
            </div>
          </div>
        </div>
      </div>

      <div className="bdr-5 bgc-4 c-red px-2 py-1 fs-18 t-medium mb-2">
        <i className="fas fa-user-plus me-2"></i>
        สมาชิก
      </div>

      <div className="border_5 bdc_primary bg_op_secondary p-3 overflow-hidden my-2 text-start">
        <div className="c_tertiary mb-2">
          <h5 className="mb-0">สมาชิก</h5>
        </div>
        <div className="d-flex align-items-stretch" style={{ gap: 10, flexWrap: 'nowrap' }}>
          <input
            type="text"
            className="form-control bg_secondary c_secondary"
            placeholder="ค้นหา username ที่ถูกปิดบัง..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, minWidth: 0, borderRadius: 12, minHeight: 46 }}
          />
          <button
            type="button"
            className="bgg-1 c-white border-0 px-3 px-md-4 t-medium"
            style={{ minWidth: 110, borderRadius: 12, whiteSpace: 'nowrap', flexShrink: 0 }}
            onClick={() => setSearchTerm(searchTerm.trim())}
          >
            <i className="fas fa-search me-2"></i>
            ค้นหา
          </button>
        </div>
      </div>

      <div className="bdr-5 bgc-4 p-2">
        <div className="row gap-2 mx--1">
          {filteredMembers.length === 0 ? (
            <div className="col-12 px-1">
              <h5 className="c-red text-center">{members.length === 0 ? 'ไม่มีข้อมูล' : 'ไม่พบสมาชิกที่ค้นหา'}</h5>
            </div>
          ) : (
            filteredMembers.map((member, index) => (
              <div key={member.username + index} className="col-12 px-1">
                <div className="bdr-5 bdc-2 bgc-4 p-2">
                  <div className="wrap c-red t-medium justify-content-between">
                    <div className="text-start">
                      <h4 className="font_md c_primary mb-0">{maskMember(member.username || member.displayName || 'สมาชิก')}</h4>
                      <small className="c-white" style={{ opacity: 0.72 }}>แสดง username แบบปิดบังบางส่วน</small>
                    </div>
                    <h6 className="">#{index + 1}</h6>
                  </div>

                  <small className="d-block mt-1 text-start c-white" style={{ opacity: 0.88 }}>
                    สมัครเมื่อ: {formatJoinDate(member.joinedAt)}{' '}
                    <span className="bgc-9 c-white px-2 py-1 bdr-10 ms-1 d-inline-block">
                      {member.status || 'สมาชิกปกติ'}
                    </span>
                  </small>

                  <div
                    className="d-grid gap-2 mt-1"
                    style={{
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))'
                    }}
                  >
                    <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                      <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                        <small>เงินเดิมพันทั้งหมด</small>
                      </div>
                      <div className="px-1 t-regular c-white">
                        <h4>{formatCurrency(0)}</h4>
                      </div>
                    </div>

                    <div className="bgg-1 bdr-5 bdc-1 p-1 text-center">
                      <div className="bgc-4 px-1 py-1 t-regular bdr-5">
                        <small>สรุปผลแพ้/ชนะ</small>
                      </div>
                      <div className="px-1 t-regular c-white">
                        <h4>{formatCurrency(0)}</h4>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}

export default AffiliateMemberPage
